import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase/client';
import { Loader2, Eye, EyeOff, CheckCircle, AlertCircle, Mail } from 'lucide-react';
import logoEmblem from '@/assets/logos/vehix-logo-emblem.png';

export const InvitePage: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExchanging, setIsExchanging] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [hasValidSession, setHasValidSession] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Handle PKCE code exchange and session detection (following Supabase guidance)
  useEffect(() => {
    const handleSessionExchange = async () => {
      setIsExchanging(true);
      setError(null);

      try {
        // Check for PKCE code in query params (primary method for flowType: 'pkce')
        const code = searchParams.get('code');
        const type = searchParams.get('type');

        // If we have a code (PKCE flow), exchange it for a session
        if (code) {
          console.log('[InvitePage] Exchanging PKCE code for session...');
          const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);

          if (exchangeError) {
            console.error('[InvitePage] Code exchange error:', exchangeError);
            setError(
              'This invite link is invalid or has expired. Please contact your administrator for a new invite.'
            );
            setIsExchanging(false);
            return;
          }

          if (data?.session) {
            console.log('[InvitePage] Session established from code exchange');
            setHasValidSession(true);
            setIsExchanging(false);
            // Clean up URL - remove code and type params
            window.history.replaceState({}, '', window.location.pathname);
            return;
          }
        }

        // Check for hash tokens (fallback for non-PKCE flows or if detectSessionInUrl processed them)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const hashType = hashParams.get('type');

        if (accessToken && (hashType === 'invite' || type === 'invite')) {
          console.log('[InvitePage] Setting session from hash tokens...');
          const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || ''
          });

          if (sessionError) {
            console.error('[InvitePage] Session error:', sessionError);
            setError(
              'This invite link is invalid or has expired. Please contact your administrator for a new invite.'
            );
            setIsExchanging(false);
            return;
          }

          if (sessionData?.session) {
            console.log('[InvitePage] Session established from hash tokens');
            setHasValidSession(true);
            setIsExchanging(false);
            // Clean up URL
            window.history.replaceState({}, '', window.location.pathname);
            return;
          }
        }

        // Check if we already have a valid session (user might have clicked link while logged in)
        // With detectSessionInUrl: true, Supabase may have already processed the tokens
        const {
          data: { session },
          error: sessionCheckError
        } = await supabase.auth.getSession();

        if (sessionCheckError) {
          console.error('[InvitePage] Session check error:', sessionCheckError);
          setError(
            'This invite link is invalid or has expired. Please contact your administrator for a new invite.'
          );
          setIsExchanging(false);
          return;
        }

        if (session) {
          // Session exists - allow password set (invite was processed)
          console.log('[InvitePage] Session found, allowing password set');
          setHasValidSession(true);
          setIsExchanging(false);
        } else {
          // No code, no hash tokens, no session
          setError(
            'This invite link is invalid or has expired. Please contact your administrator for a new invite.'
          );
          setIsExchanging(false);
        }
      } catch (err) {
        console.error('[InvitePage] Error during session exchange:', err);
        setError(
          'An error occurred while processing your invite link. Please contact your administrator for a new invite.'
        );
        setIsExchanging(false);
      }
    };

    handleSessionExchange();
  }, [searchParams]);

  // Redirect after successful password set
  useEffect(() => {
    if (success) {
      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 2000);
    }
  }, [success, navigate]);

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(pwd)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(pwd)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(pwd)) {
      return 'Password must contain at least one number';
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) {
      return 'Password must contain at least one special character';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    const validationError = validatePassword(password);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setIsSubmitting(true);

      // Update user password using Supabase Auth (following Supabase guidance)
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) {
        throw updateError;
      }

      setSuccess(true);

      // Update user metadata to track password was set
      await supabase.auth.updateUser({
        data: {
          password_set_at: new Date().toISOString()
        }
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading state while exchanging code/session
  if (isExchanging) {
    return (
      <div className='min-h-screen bg-[#f3f0e8] flex items-center justify-center p-4'>
        <div className='w-full max-w-md bg-white rounded-lg shadow-lg p-8 text-center'>
          <div className='flex justify-center mb-4'>
            <img src={logoEmblem} alt='Vehix Logo' className='w-32 h-32' />
          </div>
          <Loader2 className='h-12 w-12 text-[#d4854a] mx-auto mb-4 animate-spin' />
          <h1 className='text-2xl font-bold text-gray-900 mb-2'>Validating Your Invite</h1>
          <p className='text-gray-600'>Please wait while we verify your invitation link...</p>
        </div>
      </div>
    );
  }

  // Show success state
  if (success) {
    return (
      <div className='min-h-screen bg-[#f3f0e8] flex items-center justify-center p-4'>
        <div className='w-full max-w-md bg-white rounded-lg shadow-lg p-8 text-center'>
          <div className='flex justify-center mb-4'>
            <img src={logoEmblem} alt='Vehix Logo' className='w-32 h-32' />
          </div>
          <CheckCircle className='h-16 w-16 text-green-500 mx-auto mb-4' />
          <h1 className='text-2xl font-bold text-gray-900 mb-2'>Password Set Successfully!</h1>
          <p className='text-gray-600 mb-4'>
            Your password has been set. Redirecting you to the dashboard...
          </p>
        </div>
      </div>
    );
  }

  // Show error state if no valid session
  if (!hasValidSession) {
    return (
      <div className='min-h-screen bg-[#f3f0e8] flex items-center justify-center p-4'>
        <div className='w-full max-w-md bg-white rounded-lg shadow-lg p-8 text-center'>
          <div className='flex justify-center mb-4'>
            <img src={logoEmblem} alt='Vehix Logo' className='w-32 h-32' />
          </div>
          <AlertCircle className='h-16 w-16 text-red-500 mx-auto mb-4' />
          <h1 className='text-2xl font-bold text-gray-900 mb-2'>Invalid Invite Link</h1>
          <p className='text-gray-600 mb-4'>
            {error ||
              'This invite link is invalid or has expired. Invite links are one-time use and short-lived.'}
          </p>
          <div className='space-y-2'>
            <button
              onClick={() => navigate('/auth')}
              className='w-full bg-[#d4854a] text-white py-2 px-4 rounded-md hover:bg-[#c17640] transition-colors font-medium'
            >
              Go to Login
            </button>
            <p className='text-sm text-gray-500'>
              If you need a new invite, please contact your administrator.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show password set form
  return (
    <div className='min-h-screen bg-[#f3f0e8] flex items-center justify-center p-4'>
      <div className='w-full max-w-md bg-white rounded-lg shadow-lg p-8'>
        <div className='text-center mb-6'>
          <div className='flex justify-center mb-4'>
            <img src={logoEmblem} alt='Vehix Logo' className='w-32 h-32' />
          </div>
          <div className='flex items-center justify-center mb-2'>
            <Mail className='h-6 w-6 text-[#d4854a] mr-2' />
            <h1 className='text-2xl font-bold text-gray-900'>You're Invited!</h1>
          </div>
          <p className='text-gray-600'>
            Welcome to Vehix.ai! Please set a secure password for your account.
          </p>
        </div>

        {error && (
          <div className='mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2'>
            <AlertCircle className='h-5 w-5 text-red-600 flex-shrink-0' />
            <p className='text-sm text-red-600'>{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className='space-y-4'>
          <div>
            <label htmlFor='password' className='block text-sm font-medium text-gray-900 mb-2'>
              Password
            </label>
            <div className='relative'>
              <input
                id='password'
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder='Enter your password'
                className='w-full px-3 py-2 pr-10 border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#d4854a] focus:border-transparent'
                required
                disabled={isSubmitting}
                autoFocus
              />
              <button
                type='button'
                onClick={() => setShowPassword(!showPassword)}
                className='absolute right-3 top-2.5 text-gray-500 hover:text-gray-700'
                disabled={isSubmitting}
              >
                {showPassword ? <EyeOff className='h-5 w-5' /> : <Eye className='h-5 w-5' />}
              </button>
            </div>
            <p className='mt-1 text-xs text-gray-500'>
              Must be at least 8 characters with uppercase, lowercase, number, and special character
            </p>
          </div>

          <div>
            <label
              htmlFor='confirmPassword'
              className='block text-sm font-medium text-gray-900 mb-2'
            >
              Confirm Password
            </label>
            <div className='relative'>
              <input
                id='confirmPassword'
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder='Confirm your password'
                className='w-full px-3 py-2 pr-10 border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#d4854a] focus:border-transparent'
                required
                disabled={isSubmitting}
              />
              <button
                type='button'
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className='absolute right-3 top-2.5 text-gray-500 hover:text-gray-700'
                disabled={isSubmitting}
              >
                {showConfirmPassword ? <EyeOff className='h-5 w-5' /> : <Eye className='h-5 w-5' />}
              </button>
            </div>
          </div>

          <button
            type='submit'
            disabled={isSubmitting || !password || !confirmPassword}
            className='w-full bg-[#d4854a] text-white py-2 px-4 rounded-md hover:bg-[#c17640] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2'
          >
            {isSubmitting ? (
              <>
                <Loader2 className='h-4 w-4 animate-spin' />
                Setting Password...
              </>
            ) : (
              'Set Password'
            )}
          </button>
        </form>

        <div className='mt-6 text-center'>
          <p className='text-sm text-gray-600'>
            Need help?{' '}
            <a href='/support' className='text-[#d4854a] hover:text-[#c17640] font-semibold'>
              Contact Support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};
